import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, X, ChevronDown } from "lucide-react";
import CampoFirma from "@/components/CampoFirma";

const SECTIONS = ["Dati generali", "Sicurezza cantiere", "DPI", "Attrezzature e documenti", "Extra", "Foto e firma"];

const S2 = [["segnaletica_ok", "Segnaletica correttamente posizionata"],
  ["cantiere_delimitato", "Cantiere delimitato e accesso controllato"],
  ["cassetta_ps_ok", "Cassetta primo soccorso presente e revisionata"],
  ["estintori_ok", "Estintori presenti e revisionati"],
  ["materiale_assorbente_ok", "Materiale assorbente presente"],
  ["esplosimetro_ok", "Esplosimetro presente e revisionato"],
  ["figure_sicurezza_presenti", "Presenti le 3 figure (Preposto, PS, Emergenze)"]];
const S4 = [["macchine_idonee", "Macchine e attrezzature idonee"],
  ["strumenti_tarati", "Strumenti di misura regolarmente tarati"],
  ["documentazione_tecnica_ok", "Documentazione tecnica POS + I.O. presente"],
  ["schede_sicurezza_ok", "Schede di sicurezza prodotti presenti"]];
const S5 = [["presenza_scavo", "Presenza di scavo"],
  ["gruppo_elettrogeno_ok", "Gruppo elettrogeno presente e conforme"],
  ["terzisti_gestiti", "Terzisti/subappaltatori presenti e gestiti"],
  ["simulazione_effettuata", "Simulazione di emergenza effettuata oggi"]];

function Toggle({ value, onChange }) {
  const opts = [["si", "Sì"], ["no", "No"], ["nd", "N.D."]];
  const colors = { si: "bg-emerald-100 text-emerald-700", no: "bg-red-100 text-red-700", nd: "bg-slate-100 text-slate-600" };
  return (
    <div className="flex gap-1 flex-shrink-0">
      {opts.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
                className={`px-2.5 py-1 text-xs rounded border ${value === v ? colors[v] + " border-transparent" : "border-slate-200 text-slate-500"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}
function ToggleRow({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-2 py-2.5 border-b border-slate-100">
      <span className="text-sm flex-1">{label}</span>
      <Toggle value={value} onChange={onChange} />
    </div>
  );
}

export default function NewSiteCheckDialog({ defaultSiteId, defaultSiteName, onClose, onSaved }) {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(defaultSiteId || "");
  const [step, setStep] = useState(0);
  const [committente, setCommittente] = useState("");
  const [numeroCommessa, setNumeroCommessa] = useState("");
  const [attivita, setAttivita] = useState("");
  const [fase, setFase] = useState("");
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [presenti, setPresenti] = useState([]);
  const [showAllPicker, setShowAllPicker] = useState(false);
  const [empSearch, setEmpSearch] = useState("");
  const [answers, setAnswers] = useState({});
  const [dpiNote, setDpiNote] = useState("");
  const [esitoNote, setEsitoNote] = useState("");
  const [extraOpen, setExtraOpen] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [yearScenario, setYearScenario] = useState(null);
  const [selectedScenarioId, setSelectedScenarioId] = useState("");
  const [scenarioAnswers, setScenarioAnswers] = useState({});
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);
  const [firma, setFirma] = useState(null);

  useEffect(() => {
    api.get("/sites").then(({ data }) => setSites(data)).catch(() => {});
    api.get("/emergency-scenarios").then(({ data }) => setScenarios(data)).catch(() => {});
    api.get("/emergency-scenarios/year-pick").then(({ data }) => {
      setYearScenario(data);
      setSelectedScenarioId(prev => prev || data.id);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!siteId) return;
    api.get(`/sites/${siteId}`).then(({ data }) => {
      setEmployees(data.employees || []);
      setCommittente(prev => prev || data.client || "");
      setAttivita(prev => prev || data.activity_type || "");
    }).catch(() => {});
  }, [siteId]);
  useEffect(() => {
    api.get("/employees").then(({ data }) => setAllEmployees(data)).catch(() => {});
  }, []);

  const setAns = (k, v) => setAnswers(prev => ({ ...prev, [k]: v }));
  const togglePresente = (name) => setPresenti(prev => prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name]);

  const addPhotos = (e) => setPhotos([...photos, ...Array.from(e.target.files || []).slice(0, 5 - photos.length)]);
  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!siteId) { toast.error("Seleziona un cantiere"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("site_id", siteId);
    if (committente) fd.append("committente", committente);
    if (numeroCommessa) fd.append("numero_commessa", numeroCommessa);
    if (attivita) fd.append("attivita_svolta", attivita);
    if (fase) fd.append("fase_lavorativa", fase);
    fd.append("presenti", presenti.join(", "));
    fd.append("answers", JSON.stringify(answers));
    if (answers.simulazione_effettuata === "si" && selectedScenarioId) {
      fd.append("scenario_id", selectedScenarioId);
      fd.append("scenario_answers", JSON.stringify(scenarioAnswers));
    }
    if (dpiNote) fd.append("dpi_note", dpiNote);
    if (esitoNote) fd.append("esito_note", esitoNote);
    if (firma) fd.append("signature_base64", firma);
    photos.forEach(f => fd.append("photos", f));
    try {
      await api.post("/site-checks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Verifica registrata");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nell'invio"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Verifica in cantiere</DialogTitle></DialogHeader>

        <div className="flex gap-1 mb-1">
          <div className="flex-1 h-1 rounded bg-slate-100 overflow-hidden">
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${((step + 1) / SECTIONS.length) * 100}%` }} />
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-3">Sezione {step + 1} di {SECTIONS.length} · {SECTIONS[step]}</p>

        {step === 0 && (
          <div className="space-y-2">
            <div><Label>Cantiere</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">Seleziona...</option>
                {defaultSiteId && !sites.find(s => s.id === defaultSiteId) && <option value={defaultSiteId}>{defaultSiteName}</option>}
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div><Label>Committente <span className="text-slate-400 font-normal">(dal cantiere, modificabile)</span></Label><Input value={committente} onChange={(e) => setCommittente(e.target.value)} /></div>
            <div><Label>Numero commessa</Label><Input value={numeroCommessa} onChange={(e) => setNumeroCommessa(e.target.value)} /></div>
            <div><Label>Attività svolta <span className="text-slate-400 font-normal">(dal cantiere, modificabile)</span></Label><Input value={attivita} onChange={(e) => setAttivita(e.target.value)} /></div>
            <div><Label>Fase lavorativa</Label><Input value={fase} onChange={(e) => setFase(e.target.value)} placeholder="es. Scavo, Posa, Saldatura" /></div>
            <div>
              <Label>Personale presente</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {employees.length === 0 && <span className="text-xs text-slate-400">Nessun dipendente assegnato a questo cantiere.</span>}
                {employees.map(e => (
                  <button key={e.id} type="button" onClick={() => togglePresente(e.full_name)}
                          className={`px-2.5 py-1 text-xs rounded-full border ${presenti.includes(e.full_name) ? "bg-orange-500 text-white border-orange-500" : "border-slate-200"}`}>
                    {e.full_name}
                  </button>
                ))}
                {presenti.filter(p => !employees.some(e => e.full_name === p)).map(p => (
                  <button key={p} type="button" onClick={() => togglePresente(p)}
                          className="px-2.5 py-1 text-xs rounded-full border bg-orange-500 text-white border-orange-500">
                    {p}
                  </button>
                ))}
                <button type="button" onClick={() => setShowAllPicker(!showAllPicker)}
                        className="px-2.5 py-1 text-xs rounded-full border border-dashed border-slate-300 text-slate-500">
                  + Aggiungi da tutti i dipendenti
                </button>
              </div>
              {showAllPicker && (
                <div className="mt-2 border border-slate-200 rounded p-2">
                  <Input placeholder="Cerca per nome o badge..." className="h-9 text-sm mb-2"
                         value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} />
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {allEmployees
                      .filter(e => !empSearch || e.full_name.toLowerCase().includes(empSearch.toLowerCase()) || (e.badge_number || "").toLowerCase().includes(empSearch.toLowerCase()))
                      .map(e => (
                        <label key={e.id} className="flex items-center gap-2 p-1.5 text-sm hover:bg-slate-50 cursor-pointer rounded">
                          <input type="checkbox" checked={presenti.includes(e.full_name)} onChange={() => togglePresente(e.full_name)} />
                          <span>{e.full_name}</span>
                          <span className="text-xs text-slate-400 ml-auto">{e.badge_number}</span>
                        </label>
                      ))}
                    {allEmployees.length === 0 && <p className="text-xs text-slate-400 p-2">Nessun dipendente in anagrafica</p>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 1 && <div>{S2.map(([k, l]) => <ToggleRow key={k} label={l} value={answers[k]} onChange={(v) => setAns(k, v)} />)}</div>}

        {step === 2 && (
          <div className="space-y-2">
            <ToggleRow label="DPI previsti per la fase indossati correttamente" value={answers.dpi_corretti} onChange={(v) => setAns("dpi_corretti", v)} />
            <textarea rows={2} className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                      placeholder="Se No: quali DPI mancano..." value={dpiNote} onChange={(e) => setDpiNote(e.target.value)} />
          </div>
        )}

        {step === 3 && <div>{S4.map(([k, l]) => <ToggleRow key={k} label={l} value={answers[k]} onChange={(v) => setAns(k, v)} />)}</div>}

        {step === 4 && (
          <div>
            <button type="button" onClick={() => setExtraOpen(!extraOpen)}
                    className="w-full flex items-center justify-between p-2.5 border border-slate-200 rounded text-sm mb-1">
              <span>Scavo, gruppo elettrogeno, terzisti, simulazione</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${extraOpen ? "rotate-180" : ""}`} />
            </button>
            {extraOpen && (
              <div>
                {S5.filter(([k]) => k !== "simulazione_effettuata").map(([k, l]) => (
                  <ToggleRow key={k} label={l} value={answers[k]} onChange={(v) => setAns(k, v)} />
                ))}
                <ToggleRow label="Simulazione di emergenza effettuata" value={answers.simulazione_effettuata}
                           onChange={(v) => setAns("simulazione_effettuata", v)} />
                {answers.simulazione_effettuata === "si" && (
                  <div className="mt-2 p-3 border border-orange-200 bg-orange-50 rounded space-y-3">
                    <div>
                      <Label>Scenario</Label>
                      {yearScenario && (
                        <p className="text-[10px] text-orange-700 mb-1">
                          Scenario proposto per quest'anno: <strong>{yearScenario.title}</strong> — puoi scegliere anche un altro scenario dalla libreria.
                        </p>
                      )}
                      <select className="w-full h-10 border border-slate-200 rounded px-3"
                              value={selectedScenarioId}
                              onChange={(e) => { setSelectedScenarioId(e.target.value); setScenarioAnswers({}); }}>
                        {scenarios.map(sc => (
                          <option key={sc.id} value={sc.id}>{sc.category} · {sc.title}</option>
                        ))}
                      </select>
                    </div>
                    {(() => {
                      const sc = scenarios.find(x => x.id === selectedScenarioId);
                      if (!sc) return null;
                      return (
                        <div className="space-y-2">
                          <p className="text-sm text-slate-700 bg-white border border-slate-200 rounded p-2">{sc.description}</p>
                          {sc.questions.map(q => (
                            <div key={q.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-orange-100">
                              <span className="text-xs flex-1">{q.text}</span>
                              <div className="flex gap-1 flex-shrink-0">
                                {[["corretta", "Corretta", "bg-emerald-100 text-emerald-700"],
                                  ["parziale", "Parziale", "bg-amber-100 text-amber-700"],
                                  ["errata", "Errata", "bg-red-100 text-red-700"]].map(([v, l, cls]) => (
                                  <button key={v} type="button"
                                          onClick={() => setScenarioAnswers(prev => ({ ...prev, [q.id]: v }))}
                                          className={`px-2 py-1 text-[10px] rounded border ${scenarioAnswers[q.id] === v ? cls + " border-transparent" : "border-slate-200 text-slate-500"}`}>
                                    {l}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-3">
            <div>
              <input ref={photoRef} type="file" accept="image/*" multiple hidden onChange={addPhotos} />
              <Button type="button" variant="outline" className="w-full" onClick={() => photoRef.current?.click()} disabled={photos.length >= 5}>
                <Camera className="w-4 h-4 mr-2" /> Foto cantiere ({photos.length}/5)
              </Button>
              {photos.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {photos.map((f, i) => (
                    <div key={i} className="relative w-14 h-14">
                      <img src={URL.createObjectURL(f)} alt="" className="w-14 h-14 object-cover rounded border border-slate-200" />
                      <button type="button" onClick={() => removePhoto(i)} className="absolute -top-1.5 -right-1.5 bg-white border border-slate-200 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Note conclusive (opzionale)</Label>
              <textarea rows={2} className="w-full border border-slate-200 rounded px-3 py-2 text-sm" value={esitoNote} onChange={(e) => setEsitoNote(e.target.value)} />
            </div>
            <CampoFirma valore={firma} onChange={setFirma} etichetta="Firma tecnico" />
          </div>
        )}

        <DialogFooter className="gap-2">
          {step > 0 && <Button type="button" variant="outline" onClick={() => setStep(step - 1)}>Indietro</Button>}
          {step < SECTIONS.length - 1
            ? <Button type="button" onClick={() => setStep(step + 1)} className="bg-orange-500 hover:bg-orange-600 text-white">Avanti</Button>
            : <Button type="button" onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white">{busy ? "Invio..." : "Invia verifica"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
